import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { PlanUploadSchema, type PlanUpload } from "@travel/shared";
import { api, ApiError } from "../../api/client";
import { BottomSheet } from "../common/BottomSheet";
import { Button } from "../common/Button";

const SAMPLE = `{
  "title": "홍콩 가족여행",
  "timeline": [
    {
      "title": "인천국제공항 출발 - 홍콩 인터내셔널 공항 도착",
      "description": "인천공항 1터미널\\n홍콩익스프레스 UO627편",
      "startDate": "2026-05-01T13:30:00.000Z",
      "endDate": "2026-05-01T17:20:00.000Z",
      "googleMapLink": "https://maps.google.com/?q=Incheon+Airport"
    },
    {
      "title": "Spring Moon - 미슐랭 1스타 광둥요리",
      "description": "The Peninsula Hotel 1F\\n스마트 캐주얼 복장 필수!",
      "startDate": "2026-05-02T05:30:00.000Z",
      "endDate": "2026-05-02T07:00:00.000Z",
      "googleMapLink": "https://maps.google.com/?q=Spring+Moon+Peninsula+Hong+Kong"
    }
  ]
}`;

interface Props {
  open: boolean;
  onClose: () => void;
}

export function JsonUploadDialog({ open, onClose }: Props) {
  const [json, setJson] = useState(SAMPLE);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: (body: PlanUpload) => api.createPlan(body),
    onSuccess: ({ id }) => {
      qc.invalidateQueries({ queryKey: ["plans"] });
      onClose();
      setJson(SAMPLE);
      setError(null);
      navigate(`/plans/${id}`);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.issues) {
        setError(e.issues.map((i) => `${i.path}: ${i.message}`).join("\n"));
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError("저장에 실패했습니다.");
      }
    },
  });

  function onSave() {
    setError(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(json);
    } catch (e) {
      setError(`JSON 형식 오류: ${(e as Error).message}`);
      return;
    }
    const result = PlanUploadSchema.safeParse(parsed);
    if (!result.success) {
      setError(
        result.error.issues
          .map((i) => `${i.path.join(".")}: ${i.message}`)
          .join("\n"),
      );
      return;
    }
    mutation.mutate(result.data);
  }

  return (
    <BottomSheet open={open} onClose={onClose} title="JSON으로 계획 만들기">
      <div className="px-4 space-y-3 pb-4">
        <p className="text-sm text-slate-500">
          아래 양식에 맞춰 JSON을 붙여넣고 저장하면, 일정과 항목들이 한 번에 등록됩니다. 시간은
          ISO-8601 UTC(끝의 Z)로 입력하세요.
        </p>
        <textarea
          value={json}
          onChange={(e) => setJson(e.target.value)}
          className="w-full h-72 p-3 border border-slate-300 rounded-xl text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-slate-900"
          spellCheck={false}
        />
        {error && (
          <pre className="bg-rose-50 text-rose-700 text-xs rounded-xl p-3 whitespace-pre-wrap">
            {error}
          </pre>
        )}
        <div className="flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            취소
          </Button>
          <Button className="flex-1" onClick={onSave} disabled={mutation.isPending}>
            {mutation.isPending ? "저장 중…" : "저장"}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}
